import { z } from 'zod';
/**
 * Closed vocabularies from the Microsoft Commerce Incentives guide.
 *
 * Values in this file drive branches in the engine, so they are CHECK-constrained
 * in Postgres and mirrored here as Zod enums. Labels and groupings that admins may
 * extend without a deploy (area names, phase names, band size labels) live in
 * versioned lookup tables instead — see code_practise.md §3.
 */
/** Solution areas. `promos` is a UI-only tab; no engagement carries it. */
export const AreaKey = z.enum(['azure', 'abs', 'security', 'marketplace', 'promos']);
/** Areas an engagement can actually belong to. */
export const EngagementAreaKey = z.enum(['azure', 'abs', 'security', 'marketplace']);
/** Customer segment. Only the two Marketplace engagements admit Strategics. */
export const Segment = z.enum(['Majors', 'SMC-Corporate', 'SMB', 'Strategics']);
/**
 * Payment market.
 *   A — Australia, NZ, UK, US, EU
 *   B — all other eligible
 *   C — where a third rate applies; falls back to B when the band has no C rate.
 */
export const Market = z.enum(['A', 'B', 'C']);
/** What an engagement's band is measured against. */
export const Basis = z.enum(['acr', 'seats', 'acv', 'mpu']);
/**
 * Exclusivity group. Members compete: the highest-paying eligible member becomes
 * the recommendation and the rest become alternatives. An engagement with no
 * group stacks on top of the recommendation.
 */
export const ExclusivityGroup = z.enum(['pre', 'post']);
/** Specialization group, used by `anySpecGroup` and `minSpecCount`. */
export const SpecGroup = z.enum(['azure', 'modernwork', 'security', 'bizapps']);
/**
 * Specialization status. `held()` counts `enrolled` always, and `qualified` only
 * when the user has switched on "treat Qualified as eligible" — a what-if toggle
 * for modelling the effect of completing enrolment.
 */
export const SpecStatus = z.enum(['enrolled', 'qualified', 'progress', 'none']);
/** Designation status. There is no `qualified` — only `enrolled` counts. */
export const DesignationStatus = z.enum(['enrolled', 'progress', 'none']);
/** Qualifying-service bucket a sub-floor is measured against. */
export const SubFloorKey = z.enum(['mdc', 'db', 'agentic']);
/**
 * Workload family. An engagement is "relevant" when at least one of its families
 * has non-zero incremental spend, and is sized on the sum of its own families.
 */
export const FamilyKey = z.enum([
    'core',
    'db',
    'fabric',
    'aiplat',
    'avs',
    'avd',
    'sap',
    'sov',
    'mdc',
    'sentinel',
]);
/**
 * Two engagements size on a single service rather than on their families:
 * the Sentinel and Defender for Cloud accelerators.
 */
export const SizeOnKey = z.enum(['mdc', 'sentinel']);
/** Customer type, used by the pre-qualification checks. */
export const CustomerType = z.enum(['commercial', 'edu', 'nonprofit', 'strategic500']);
/** Currency an amount was entered in. */
export const Currency = z.enum(['AUD', 'USD']);
/** Whether workload amounts are entered per month or per year. */
export const AcrPeriod = z.enum(['month', 'year']);
/**
 * How Azure consumption is entered.
 *   workload — a per-workload breakdown; enables family sizing and the relevance gate
 *   single   — one annual figure; no family breakdown, so no relevance gate
 */
export const AcrMode = z.enum(['workload', 'single']);
/**
 * The nineteen pre-qualification predicates. Ids live in data; the bodies live in
 * `packages/engine/src/checks/registry.ts`. The registry is asserted complete
 * against this list at boot, so a guide referencing an unimplemented check fails
 * loudly rather than silently skipping the gate (which is what the legacy did).
 */
export const CheckId = z.enum([
    'not_strat',
    'not_edu_np',
    'no_edu_np_small',
    'no_edu_np_xxs',
    'intune_le50',
    'intune_ge40',
    'entra_80',
    'copilot_ws',
    'csp_terms',
    'net_adds',
    'timing',
    'caps',
    'fy26_ok',
    'propensity',
    'ea_e5',
    'mxdr_misa',
    'mpu_usage_50',
    'mpu_usage_40',
    'copilot_spec_2027',
]);
/** Labels shown on a gate row in the results list. */
export const GateLabel = z.enum([
    'Partner capability',
    'Customer segment',
    'Deal size floor',
    'Qualifying-service sub-floor',
    'Workload relevance',
    'Pre-qualification',
    'Paired ISV partner',
    'Prior engagement',
]);
/** How a row was picked within its exclusivity group. */
export const PickKind = z.enum(['recommended', 'alternative']);
//# sourceMappingURL=domain.js.map